/**
 * Storefront home composition — the typed section registry (spec 42 R1/R2).
 *
 * A store's home page is an ordered list of typed sections. This module is the
 * single definition of what a section may be: the ERP composer validates
 * against it before staging a version, the API validates again before insert,
 * and the storefront renders from the parsed result.
 *
 * Two properties matter more than the field list:
 *
 *  - **Unknown types are skipped, never fatal** (R1). A published version must
 *    keep rendering after an app rollback, so the renderer tolerates a `type`
 *    it has never heard of. `parseSections` therefore drops unparseable
 *    entries rather than throwing — validation of *new* input is
 *    `sectionSchema`'s job, at the write boundary.
 *  - **Link targets are typed objects, never free URLs into our own site**
 *    (R3), so a dangling collection can drop its CTA instead of shipping a 404.
 *
 * @see docs/spec/42-store-front-home-composer.md
 */
import { z } from "zod";

// ── Link targets (R3) ──────────────────────────────────────────

/**
 * `https://` only. `http:`, `javascript:`, `data:` and protocol-relative
 * hrefs are rejected at the boundary — this is merchant-authored input that
 * ends up in an anchor on a public page.
 */
const externalHref = z
  .string()
  .trim()
  .max(2048)
  .refine((v) => v.startsWith("https://"), {
    message: "Links must start with https://",
  });

export const linkTargetSchema = z.discriminatedUnion("kind", [
  z.object({ kind: z.literal("collection"), slug: z.string().trim().min(1).max(64) }),
  z.object({ kind: z.literal("product"), slug: z.string().trim().min(1).max(64) }),
  z.object({ kind: z.literal("page"), slug: z.string().trim().min(1).max(64) }),
  z.object({ kind: z.literal("search"), q: z.string().trim().max(120).optional() }),
  z.object({ kind: z.literal("whatsapp") }),
  z.object({ kind: z.literal("url"), href: externalHref }),
]);
export type LinkTarget = z.infer<typeof linkTargetSchema>;

const ctaSchema = z.object({
  label: z.string().trim().min(1).max(24),
  target: linkTargetSchema,
});
export type SectionCta = z.infer<typeof ctaSchema>;

/**
 * Plain text (R8) — stored as entered, rendered escaped. `.trim()` only; no
 * markup is stripped here because none is ever interpreted downstream.
 */
const plainText = (max: number) => z.string().trim().max(max);

/** A stored image is a public URL, like `store_listings.images` (R6). */
const imageUrl = z.string().trim().max(2048);

// ── The eight section types (R2) ───────────────────────────────

/**
 * Per-section background scheme (module 22 D4). Applied by the renderer as a
 * full-width band that *remaps the theme tokens* for its subtree, so the same
 * section component reads legibly on a light, accent or dark band without any
 * per-section colour code. Absent / "default" → no band (the page background).
 * Ignored on the hero (it owns its look); the full-bleed announcement bar reads
 * it directly to self-colour, so it is a section-only override there.
 */
export const sectionScheme = z.enum(["default", "muted", "accent", "dark"]);
export type SectionScheme = z.infer<typeof sectionScheme>;

const baseFields = {
  id: z.string().uuid(),
  enabled: z.boolean(),
  scheme: sectionScheme.optional(),
};

export const announcementBarSchema = z.object({
  ...baseFields,
  type: z.literal("announcement_bar"),
  text: plainText(120).min(1),
  target: linkTargetSchema.optional(),
  tone: z.enum(["info", "promo"]),
  dismissible: z.boolean(),
});

export const heroSlideSchema = z.object({
  imageUrl: imageUrl.optional(),
  headline: plainText(60).min(1),
  subhead: plainText(120).optional(),
  /** Up to two — "Browse AW26" plus "WhatsApp {phone}" is the canonical pair. */
  ctas: z.array(ctaSchema).max(2),
});
export type HeroSlide = z.infer<typeof heroSlideSchema>;

export const heroSliderSchema = z.object({
  ...baseFields,
  type: z.literal("hero_slider"),
  slides: z.array(heroSlideSchema).min(1).max(5),
  /** 0 = no autoplay. Suspended entirely under `prefers-reduced-motion` (R13). */
  autoplayMs: z.union([z.literal(0), z.literal(4000), z.literal(6000), z.literal(8000)]),
});

export const categoryGridSchema = z.object({
  ...baseFields,
  type: z.literal("category_grid"),
  /** Collections only — `items.category` holds input materials, not garment
   *  categories, and would misrepresent the store (spec 42 Q2). */
  source: z.literal("collections"),
  style: z.enum(["circles", "cards"]),
  limit: z.number().int().min(4).max(12),
  showCounts: z.boolean(),
  heading: plainText(40).min(1),
  subheading: plainText(120).optional(),
});

const productGridSourceSchema = z.union([
  z.object({ kind: z.literal("collection"), slug: z.string().trim().min(1).max(64) }),
  z.object({ kind: z.literal("newest") }),
  z.object({ kind: z.literal("uncollected") }),
]);

export const productGridSchema = z.object({
  ...baseFields,
  type: z.literal("product_grid"),
  source: productGridSourceSchema,
  /** Caps at 8 — spec 24 R4 owns the home page-size cap. */
  limit: z.union([z.literal(4), z.literal(8)]),
  heading: plainText(40).min(1),
  subheading: plainText(120).optional(),
  footerLink: ctaSchema.optional(),
});

export const collectionRailSchema = z.object({
  ...baseFields,
  type: z.literal("collection_rail"),
  source: z.union([
    z.object({ kind: z.literal("collection"), slug: z.string().trim().min(1).max(64) }),
    z.object({ kind: z.literal("all-visible") }),
  ]),
});

export const promoBannerSchema = z.object({
  ...baseFields,
  type: z.literal("promo_banner"),
  imageUrl: imageUrl.optional(),
  eyebrow: plainText(32).optional(),
  headline: plainText(60).min(1),
  body: plainText(240).optional(),
  bullets: z.array(plainText(60).min(1)).max(4).optional(),
  cta: ctaSchema.optional(),
  imageSide: z.enum(["left", "right"]),
});

/** One seller-authored trust tile — a short value and its caption. */
const statItemSchema = z.object({
  value: plainText(24).min(1),
  label: plainText(24).min(1),
});
export type StatItem = z.infer<typeof statItemSchema>;

/**
 * The trust row under the hero (spec 28 R3).
 *
 * Two modes, both honest: with `items` the seller states their own figures
 * (their facts, not ours to invent); with `items` empty or absent it falls back
 * to whichever store-profile fields are set — the original behaviour. An
 * optional `heading` gives it a title like every other content section.
 */
export const statsStripSchema = z.object({
  ...baseFields,
  type: z.literal("stats_strip"),
  heading: plainText(40).optional(),
  items: z.array(statItemSchema).max(4).optional(),
});

/**
 * The seller's story (spec 28 R4). Like the stats strip, two honest modes:
 * with `heading`/`body` set the seller writes it here; with them blank it falls
 * back to the store name and the profile's About text — the original behaviour.
 */
export const aboutBlockSchema = z.object({
  ...baseFields,
  type: z.literal("about_block"),
  heading: plainText(60).optional(),
  body: plainText(1200).optional(),
});

// ── Module 22 D3: additional merchant-authored sections ────────

/** Freeform heading + body, left- or centre-aligned. Pure text (R8). */
export const richTextSchema = z.object({
  ...baseFields,
  type: z.literal("rich_text"),
  heading: plainText(60).min(1),
  body: plainText(2000).min(1),
  align: z.enum(["left", "center"]),
});

/** Side-by-side image and copy, with an optional CTA. */
export const imageTextSchema = z.object({
  ...baseFields,
  type: z.literal("image_text"),
  imageUrl: imageUrl.optional(),
  heading: plainText(60).min(1),
  body: plainText(500).optional(),
  imageSide: z.enum(["left", "right"]),
  cta: ctaSchema.optional(),
});

const testimonialItem = z.object({
  quote: plainText(280).min(1),
  author: plainText(60).min(1),
});
/** Dealer reviews — 1–6 quote/author cards. */
export const testimonialsSchema = z.object({
  ...baseFields,
  type: z.literal("testimonials"),
  heading: plainText(40).optional(),
  items: z.array(testimonialItem).min(1).max(6),
});

const faqItem = z.object({
  question: plainText(160).min(1),
  answer: plainText(600).min(1),
});
/** Common wholesale questions — 1–12 question/answer pairs. */
export const faqSchema = z.object({
  ...baseFields,
  type: z.literal("faq"),
  heading: plainText(40).optional(),
  items: z.array(faqItem).min(1).max(12),
});

/** A partner / brand logo — an image with optional alt text. */
const logoItemSchema = z.object({
  imageUrl,
  alt: plainText(60).optional(),
});
/** A row of partner or brand logos (module 22 D3). */
export const logoWallSchema = z.object({
  ...baseFields,
  type: z.literal("logo_wall"),
  heading: plainText(40).optional(),
  items: z.array(logoItemSchema).min(1).max(12),
  /** Render logos in muted greyscale until hover — a common trust-row look. */
  grayscale: z.boolean(),
});

/** An image gallery — a simple responsive grid (module 22 D3). */
export const gallerySchema = z.object({
  ...baseFields,
  type: z.literal("gallery"),
  heading: plainText(40).optional(),
  images: z.array(imageUrl).min(1).max(12),
  columns: z.union([z.literal(2), z.literal(3), z.literal(4)]),
});

/** A single-product spotlight — merchant-curated copy + image + CTA (D3). */
export const featuredProductSchema = z.object({
  ...baseFields,
  type: z.literal("featured_product"),
  eyebrow: plainText(32).optional(),
  headline: plainText(60).min(1),
  body: plainText(300).optional(),
  /** Free-text price line, e.g. "From ₹499 / pc" — the seller's own claim. */
  priceLabel: plainText(40).optional(),
  imageUrl: imageUrl.optional(),
  cta: ctaSchema.optional(),
});

/** A contact block — copy, address and an optional WhatsApp button (D3). */
export const contactSchema = z.object({
  ...baseFields,
  type: z.literal("contact"),
  heading: plainText(40).optional(),
  body: plainText(300).optional(),
  address: plainText(200).optional(),
  /** Show a "Message on WhatsApp" button (needs the store's phone). */
  showWhatsapp: z.boolean(),
});

/** A newsletter opt-in — enquiries reach the seller over WhatsApp (D3). */
export const newsletterSchema = z.object({
  ...baseFields,
  type: z.literal("newsletter"),
  heading: plainText(60).min(1),
  subtext: plainText(160).optional(),
  buttonLabel: plainText(24).optional(),
});

/** A dealer / lead-capture form — enquiries reach the seller over WhatsApp (D3). */
export const leadCaptureSchema = z.object({
  ...baseFields,
  type: z.literal("lead_capture"),
  heading: plainText(60).min(1),
  subtext: plainText(160).optional(),
  buttonLabel: plainText(24).optional(),
});

export const sectionSchema = z.discriminatedUnion("type", [
  announcementBarSchema,
  heroSliderSchema,
  categoryGridSchema,
  productGridSchema,
  collectionRailSchema,
  promoBannerSchema,
  statsStripSchema,
  aboutBlockSchema,
  richTextSchema,
  imageTextSchema,
  testimonialsSchema,
  faqSchema,
  logoWallSchema,
  gallerySchema,
  featuredProductSchema,
  contactSchema,
  newsletterSchema,
  leadCaptureSchema,
]);
export type HomeSection = z.infer<typeof sectionSchema>;
export type HomeSectionType = HomeSection["type"];

/** Types that may appear at most once on a page (R10). */
export const SINGLETON_SECTION_TYPES = [
  "announcement_bar",
  "hero_slider",
  "stats_strip",
  "about_block",
] as const satisfies readonly HomeSectionType[];

/** Hard ceiling on a version's section count (R10). */
export const MAX_SECTIONS = 12;

/**
 * A whole staged composition. Enforces the caps R10 states, so both the
 * composer and the API get the same errors from one place.
 */
export const sectionsSchema = z
  .array(sectionSchema)
  .max(MAX_SECTIONS, { message: `A home page can have at most ${MAX_SECTIONS} sections.` })
  .superRefine((sections, ctx) => {
    for (const type of SINGLETON_SECTION_TYPES) {
      if (sections.filter((s) => s.type === type).length > 1) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: `Only one ${SECTION_LABELS[type].toLowerCase()} per page.`,
        });
      }
    }
  });

/** Human labels — used in the composer list, the Add menu and error copy. */
export const SECTION_LABELS: Record<HomeSectionType, string> = {
  announcement_bar: "Announcement bar",
  hero_slider: "Hero slider",
  category_grid: "Category grid",
  product_grid: "Product grid",
  collection_rail: "Collection rail",
  promo_banner: "Promo banner",
  stats_strip: "Stats strip",
  about_block: "About block",
  rich_text: "Rich text",
  image_text: "Image with text",
  testimonials: "Testimonials",
  faq: "FAQ",
  logo_wall: "Logo wall",
  gallery: "Gallery",
  featured_product: "Featured product",
  contact: "Contact",
  newsletter: "Newsletter",
  lead_capture: "Dealer signup",
};

/**
 * Parse a stored version's `sections`, **dropping** anything that doesn't
 * validate (R1: unknown or invalid types are skipped, never fatal). Use
 * `sectionsSchema` — not this — to validate input at a write boundary.
 */
export function parseSections(raw: unknown): HomeSection[] {
  if (!Array.isArray(raw)) return [];
  const out: HomeSection[] = [];
  for (const entry of raw) {
    const parsed = sectionSchema.safeParse(entry);
    if (parsed.success) out.push(parsed.data);
  }
  return out;
}

// ── Default composition (R5) ───────────────────────────────────

/**
 * What a store renders before anyone opens the composer — and whenever a
 * published version has no enabled section. Deliberately a constant, not a
 * seeded row: a store must never depend on data it didn't create.
 *
 * This reproduces spec 27's page exactly (hero → rails → all-products with its
 * "Browse all →" footer), so shipping the composer regresses no store.
 *
 * Ids are fixed rather than random: the payload must be stable across renders
 * so React keys and the composer's "this is the default" list don't churn.
 */
export const DEFAULT_HOME_SECTION_IDS = {
  hero: "00000000-0000-4000-8000-000000000001",
  stats: "00000000-0000-4000-8000-000000000002",
  about: "00000000-0000-4000-8000-000000000003",
  rail: "00000000-0000-4000-8000-000000000004",
  allProducts: "00000000-0000-4000-8000-000000000005",
} as const;

export interface DefaultCompositionInput {
  storeName: string;
  tagline: string | null;
  coverImageUrl: string | null;
  /** Contact phone, when set — drives the second hero CTA (spec 28 R2). */
  phone: string | null;
}

// ── Theme / appearance (module 22, task A1) ────────────────────

/**
 * Curated per-store theme. This is the whole themeable surface — colours, a
 * font pairing and a corner radius — NOT a CSS or template field (module 22
 * baked-in decision: curated settings, not raw CSS). Each value maps to one of
 * the `.flint-store-theme` custom properties the storefront already reads, so
 * theming is "override the token", never "restyle a component".
 *
 * The theme travels *inside* a `store_home_versions` row: it stages and
 * publishes atomically with the sections, reusing the existing draft/publish
 * spine rather than inventing a second one.
 */
const hexColour = z
  .string()
  .trim()
  .regex(/^#[0-9a-fA-F]{6}$/, { message: "Use a 6-digit hex colour like #1c1917." });

/** Font pairings the storefront ships — heading + body families. */
export const STORE_FONT_PAIRINGS = {
  spark: {
    label: "Spark (Space Grotesk · Jakarta)",
    heading: "'Space Grotesk Variable', 'Space Grotesk', ui-sans-serif, system-ui, sans-serif",
    body: "'Plus Jakarta Sans Variable', 'Plus Jakarta Sans', ui-sans-serif, system-ui, sans-serif",
  },
  modern: {
    label: "Modern (Inter)",
    heading: "'Inter Variable', 'Inter', ui-sans-serif, system-ui, sans-serif",
    body: "'Inter Variable', 'Inter', ui-sans-serif, system-ui, sans-serif",
  },
  classic: {
    label: "Classic (serif headings)",
    heading: "Georgia, 'Times New Roman', serif",
    body: "'Inter Variable', 'Inter', ui-sans-serif, system-ui, sans-serif",
  },
  bold: {
    label: "Bold (Poppins)",
    heading: "'Poppins', ui-sans-serif, system-ui, sans-serif",
    body: "'Poppins', ui-sans-serif, system-ui, sans-serif",
  },
} as const;

export const storeFontPairing = z.enum(["spark", "modern", "classic", "bold"]);
export type StoreFontPairing = z.infer<typeof storeFontPairing>;

// ── Header / footer chrome (module 22, editor parity) ──────────

/** One custom header nav link — a label pointing at any link target. */
const headerNavItem = z.object({
  label: plainText(24).min(1),
  target: linkTargetSchema,
});
export type StoreHeaderNavItem = z.infer<typeof headerNavItem>;

/** Storefront header config. Optional on the theme; defaults below fill in. */
export const headerConfigSchema = z.object({
  /** Custom wordmark text; falls back to the store name (hidden when a logo image is set). */
  logoText: plainText(40).optional(),
  /** Curated nav links; empty → the header lists all collections automatically. */
  nav: z.array(headerNavItem).max(6).default([]),
  /**
   * When no custom nav links are set, auto-list the store's collections in the
   * menu. Off → a clean header with no nav (unless custom links are added).
   * Optional/defaulted so headers saved before it keep the auto behaviour.
   */
  autoCollectionNav: z.boolean().default(true),
  showSearch: z.boolean(),
  showCart: z.boolean(),
  showAccount: z.boolean(),
  sticky: z.boolean(),
});
export type StoreHeaderConfig = z.infer<typeof headerConfigSchema>;
export const DEFAULT_HEADER_CONFIG: StoreHeaderConfig = {
  nav: [],
  autoCollectionNav: true,
  showSearch: true,
  showCart: true,
  showAccount: true,
  sticky: true,
};

/** Storefront footer content. Plain text (R8). */
export const footerConfigSchema = z.object({
  about: plainText(300).optional(),
  tagline: plainText(120).optional(),
  /** Show a contact column (phone / city / GSTIN from the store profile). */
  showContact: z.boolean().default(false),
  /** Dark footer instead of the default light one. */
  dark: z.boolean().default(false),
});
export type StoreFooterConfig = z.infer<typeof footerConfigSchema>;
export const DEFAULT_FOOTER_CONFIG: StoreFooterConfig = { showContact: false, dark: false };

export const storeThemeSchema = z.object({
  /** Which preset seeded this theme — display only; edits are free-form. */
  preset: z.string().trim().max(32),
  accent: hexColour,
  accentFg: hexColour,
  ink: hexColour,
  muted: hexColour,
  bg: hexColour,
  surface: hexColour,
  line: hexColour,
  /** Corner radius in px — 0 (sharp) … 24 (soft). Maps to `--r`. */
  radius: z.number().int().min(0).max(24),
  fontPairing: storeFontPairing,
  /**
   * Store logo — a public URL under this company's storage prefix (uploaded via
   * the same presign path as images, so `assertOwnImages` accepts it). Absent →
   * the storefront header falls back to the wordmark. Not a CSS var; passed to
   * the header as data.
   */
  logoUrl: z.string().trim().max(2048).optional(),
  /** Logo height in px (20–72); the header scales the image to it, width auto.
   *  Optional so themes saved before it default to 28. */
  logoHeight: z.number().int().min(20).max(72).optional(),
  /** Header/footer chrome — optional so themes saved before this still parse. */
  header: headerConfigSchema.optional(),
  footer: footerConfigSchema.optional(),
  /**
   * Favicon — a public URL under this company's storage prefix, same upload
   * path as the logo. Rendered as `<link rel="icon">` on the storefront.
   */
  faviconUrl: z.string().trim().max(2048).optional(),
  /**
   * SEO / social metadata for the storefront `<head>` (module 22). All optional
   * so themes saved before they existed still parse; the storefront's `head()`
   * falls back to today's derived values (store name + suffix, tagline) when a
   * field is blank. Per-product meta comes later; this is the store default.
   *  - `metaTitle` — the full <title>/og:title; overrides `name + titleSuffix`.
   *  - `titleSuffix` — appended after the store name when `metaTitle` is blank
   *    (replaces the hard-coded " — wholesale store"). Empty string = no suffix.
   *  - `metaDescription` — meta/og description; overrides the tagline fallback.
   *  - `socialImageUrl` — og:image for link previews; overrides the hero/cover
   *    chain. A public URL under this company's storage prefix, like the logo.
   */
  metaTitle: z.string().trim().max(70).optional(),
  titleSuffix: z.string().trim().max(48).optional(),
  metaDescription: z.string().trim().max(200).optional(),
  socialImageUrl: z.string().trim().max(2048).optional(),
  /**
   * Extra layout controls (module 22 C2). All optional so themes saved before
   * they existed still parse; `themeVars`/the storefront supply the fallbacks.
   *  - `baseSize` — storefront root font size in px (13–20); every rem scales.
   *  - `containerWidth` — max content width in px → `--s-container`.
   *  - `sectionSpacing` — vertical space around each section in px → `--s-section-gap`.
   */
  baseSize: z.number().int().min(13).max(20).optional(),
  containerWidth: z.number().int().min(960).max(1440).optional(),
  sectionSpacing: z.number().int().min(16).max(96).optional(),
});
export type StoreTheme = z.infer<typeof storeThemeSchema>;

/** Storefront root font size (px) for a theme — drives every rem. */
export const DEFAULT_BASE_SIZE = 16;
export function themeBaseSize(theme: StoreTheme): number {
  return theme.baseSize ?? DEFAULT_BASE_SIZE;
}

/**
 * The default theme — values are lifted verbatim from `store-tokens-scoped.css`
 * so a store that never opened the theme editor renders exactly as today.
 */
export const defaultStoreTheme: StoreTheme = {
  preset: "spark",
  accent: "#c63f1c",
  accentFg: "#ffffff",
  ink: "#1c1917",
  muted: "#57534e",
  bg: "#fafaf9",
  surface: "#ffffff",
  line: "#e7e5e4",
  radius: 8,
  fontPairing: "spark",
};

/** Ready-made themes for the editor's preset picker (module 22, E1 seed). */
export const STORE_THEME_PRESETS: Record<string, StoreTheme> = {
  spark: defaultStoreTheme,
  minimal: {
    preset: "minimal",
    accent: "#111111",
    accentFg: "#ffffff",
    ink: "#111111",
    muted: "#737373",
    bg: "#ffffff",
    surface: "#fafafa",
    line: "#e5e5e5",
    radius: 3,
    fontPairing: "modern",
  },
  classic: {
    preset: "classic",
    accent: "#1e5f4f",
    accentFg: "#ffffff",
    ink: "#1a1a17",
    muted: "#6b6a63",
    bg: "#fcfbf7",
    surface: "#ffffff",
    line: "#e7e4d8",
    radius: 2,
    fontPairing: "classic",
  },
  bold: {
    preset: "bold",
    accent: "#db2777",
    accentFg: "#ffffff",
    ink: "#0f172a",
    muted: "#64748b",
    bg: "#fffdfa",
    surface: "#ffffff",
    line: "#f0e2ea",
    radius: 18,
    fontPairing: "bold",
  },
  // A dark storefront — the one preset whose page background is not near-white.
  midnight: {
    preset: "midnight",
    accent: "#f59e0b",
    accentFg: "#1c1917",
    ink: "#f5f5f4",
    muted: "#a8a29e",
    bg: "#0c0a09",
    surface: "#1c1917",
    line: "#292524",
    radius: 10,
    fontPairing: "modern",
  },
};

/**
 * Parse a stored version's `theme`, falling back to the default when it is
 * absent or invalid (an old version predates the column; a future preset adds a
 * field). Like `parseSections`, it never throws on read.
 */
export function parseTheme(raw: unknown): StoreTheme {
  const parsed = storeThemeSchema.safeParse(raw);
  return parsed.success ? parsed.data : defaultStoreTheme;
}

/**
 * The core theming mechanism (task A4): map a `StoreTheme` to the
 * `.flint-store-theme` / `:root` custom-property overrides the storefront
 * components already read (`var(--accent)`, `var(--r)`, …). Framework-agnostic
 * (plain `Record`, no React) so the ERP editor preview and the SSR'd storefront
 * inject the *same* variables — theming is "override the token", never
 * "restyle a component".
 */
export function themeVars(theme: StoreTheme): Record<string, string> {
  const pairing = STORE_FONT_PAIRINGS[theme.fontPairing];
  return {
    "--accent": theme.accent,
    "--accent-hover": theme.accent,
    "--accent-fg": theme.accentFg,
    "--brand": theme.accent,
    // Derived accent surfaces so token-driven primitives (the shadcn Select,
    // Dialog, Button, …) tint from the store's chosen accent instead of falling
    // back to the packaged Ember defaults. `color-mix` keeps them a shade of the
    // real accent: a pale fill for hover/selected rows, a translucent focus ring.
    "--accent-subtle": `color-mix(in srgb, ${theme.accent} 12%, #ffffff)`,
    "--ring": `color-mix(in srgb, ${theme.accent} 40%, transparent)`,
    "--ink": theme.ink,
    "--ink-2": theme.ink,
    "--muted": theme.muted,
    "--bg": theme.bg,
    "--paper": theme.surface,
    "--raised": theme.surface,
    "--line": theme.line,
    "--r-sm": `${Math.max(0, theme.radius - 2)}px`,
    "--r": `${theme.radius}px`,
    "--r-lg": `${theme.radius + 4}px`,
    "--font-display": pairing.heading,
    "--font-sans": pairing.body,
    // Layout controls (module 22 C2). Header/footer already read --s-container;
    // the section container + band + wraps read both. 48px default gives
    // storefronts generous breathing room between sections (adjustable 16–96).
    "--s-container": `${theme.containerWidth ?? 1120}px`,
    // 32, per spec 49 R9 ("absent → 16 / 1120 / 32"). It had drifted to 48,
    // which left the spec, this code and `store-appearance.test.ts` disagreeing
    // — the test was the one telling the truth.
    "--s-section-gap": `${theme.sectionSpacing ?? 32}px`,
  };
}

export function defaultHomeSections(input: DefaultCompositionInput): HomeSection[] {
  const ctas: SectionCta[] = [{ label: "Browse catalog", target: { kind: "search" } }];
  // Spec 28 R2: the WhatsApp CTA is independent of every other profile field.
  if (input.phone) ctas.push({ label: "WhatsApp", target: { kind: "whatsapp" } });

  return [
    {
      id: DEFAULT_HOME_SECTION_IDS.hero,
      type: "hero_slider",
      enabled: true,
      slides: [
        {
          ...(input.coverImageUrl ? { imageUrl: input.coverImageUrl } : {}),
          headline: input.storeName,
          ...(input.tagline ? { subhead: input.tagline } : {}),
          ctas,
        },
      ],
      autoplayMs: 0,
    },
    { id: DEFAULT_HOME_SECTION_IDS.stats, type: "stats_strip", enabled: true },
    { id: DEFAULT_HOME_SECTION_IDS.about, type: "about_block", enabled: true },
    {
      id: DEFAULT_HOME_SECTION_IDS.rail,
      type: "collection_rail",
      enabled: true,
      source: { kind: "all-visible" },
    },
    {
      id: DEFAULT_HOME_SECTION_IDS.allProducts,
      type: "product_grid",
      enabled: true,
      source: { kind: "uncollected" },
      limit: 8,
      heading: "All products",
      footerLink: { label: "Browse all", target: { kind: "search" } },
    },
  ];
}
